import React, { useEffect, useState } from "react";
import { Plus, Trash2, GraduationCap, X, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Badge, SearchInput, EmptyState, Card } from "@/components/ui-em";

export default function AdminAcademy() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    duration_min: 30,
    difficulty: "Beginner",
    video_url: "",
    instructor: "",
    category: "General",
  });

  const load = () => {
    setLoading(true);
    api
      .get("/academy/trainings")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((t) =>
    !q ? true : t.title.toLowerCase().includes(q.toLowerCase()) || (t.description || "").toLowerCase().includes(q.toLowerCase())
  );

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/academy/trainings", form);
      toast.success("Training created");
      setShowForm(false);
      setForm({ title: "", description: "", duration_min: 30, difficulty: "Beginner", video_url: "", instructor: "", category: "General" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this training?")) return;
    try {
      await api.delete(`/academy/trainings/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Content"
        title="Academy Management"
        description="Publish trainings, add instructors, structure the learning path."
        actions={<Button icon={Plus} onClick={() => setShowForm(true)} size="lg" data-testid="admin-new-training">New training</Button>}
      />

      <SearchInput value={q} onChange={setQ} placeholder="Search trainings..." />

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-zinc-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title="No trainings yet"
            description="Add training modules so your team can start learning."
            actionLabel="Create first training"
            onAction={() => setShowForm(true)}
            className="m-4"
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold text-left">
                <th className="px-6 py-4">Title</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Difficulty</th>
                <th className="px-6 py-4">Duration</th>
                <th className="px-6 py-4">Instructor</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-white/5 hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "140ms" }}>
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">{t.title}</div>
                    <div className="text-xs text-zinc-500 line-clamp-1 max-w-md">{t.description}</div>
                  </td>
                  <td className="px-6 py-4 text-xs text-gold">{t.category || "General"}</td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" uppercase>{t.difficulty}</Badge>
                  </td>
                  <td className="px-6 py-4 text-xs text-zinc-400 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {t.duration_min}m
                  </td>
                  <td className="px-6 py-4 text-xs text-zinc-400">{t.instructor || "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => remove(t.id)}
                      className="p-2 rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-500/10"
                      style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-ink-0/70 backdrop-blur-md">
          <div className="em-card p-8 w-full max-w-xl" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.7)" }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-display text-xl">New training</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="em-input" data-testid="training-title" />
              <textarea required placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="em-input" rows={3} data-testid="training-desc" />
              <div className="grid grid-cols-2 gap-3">
                <input type="number" min={1} placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })} className="em-input" />
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="em-input">
                  <option>Beginner</option>
                  <option>Intermediate</option>
                  <option>Advanced</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Instructor (optional)" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} className="em-input" />
                <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="em-input" />
              </div>
              <input placeholder="Video URL (optional)" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className="em-input" />
              <Button type="submit" variant="primary" size="lg" className="w-full" data-testid="training-submit">Create training</Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
